import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  School as SchoolIcon,
  AccessTime as PendingIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import api from "../services/api";

interface Scholarship {
  _id: string;
  fullName: string;
  cnic: string;
  rollNumber: string;
  email: string;
  mobileNumber: string;
  challanNumber: string;
  imagePath?: string;
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
  createdAt: string;
  updatedAt: string;
}

interface ScholarshipResponse {
  scholarships: Scholarship[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

interface ScholarshipStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  recent: number;
}

function Scholarships() {
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [stats, setStats] = useState<ScholarshipStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedScholarshipName, setSelectedScholarshipName] =
    useState<string>("");
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [selectedScholarship, setSelectedScholarship] =
    useState<Scholarship | null>(null);
  const [newStatus, setNewStatus] = useState<"approved" | "rejected">(
    "approved"
  );
  const [updateReason, setUpdateReason] = useState("");

  useEffect(() => {
    fetchScholarships();
  }, [page, rowsPerPage, search, status, startDate, endDate]);

  useEffect(() => {
    fetchScholarshipStats();
  }, []);

  const fetchScholarships = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (search) params.search = search;
      if (status !== "") params.status = status;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get("/scholarships", { params });
      const data: ScholarshipResponse = response.data.data;
      setScholarships(data.scholarships);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error("Error fetching scholarships:", err);
      setError("Failed to load scholarships");
    } finally {
      setLoading(false);
    }
  };

  const fetchScholarshipStats = async () => {
    try {
      const response = await api.get("/scholarships/stats");
      setStats(response.data.data);
    } catch (err) {
      console.error("Error fetching scholarship stats:", err);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const downloadScholarships = async () => {
    try {
      setLoading(true);

      // Build the same params as the fetchScholarships function
      const params: any = {};

      if (search) params.search = search;
      if (status !== "") params.status = status;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      // Call the download endpoint
      const response = await api.get("/scholarships/download", {
        params,
        responseType: "blob", // Important for file downloads
      });

      // Create download link
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with current date
      const timestamp = new Date().toISOString().split("T")[0];
      const filterInfo = getActiveFilterCount() > 0 ? "_filtered" : "_all";
      link.download = `scholarships_export${filterInfo}_${timestamp}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading scholarships:", err);
      setError("Failed to download scholarships data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-PK");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
      default:
        return "warning";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircleIcon color="success" />;
      case "rejected":
        return <CancelIcon color="error" />;
      case "pending":
      default:
        return <PendingIcon color="warning" />;
    }
  };

  const getFilterSummary = () => {
    const hasFilters = search || status !== "" || startDate || endDate;
    const totalDbScholarships = stats?.total || 0;

    if (!hasFilters) {
      return `Total: ${totalDbScholarships.toLocaleString()} scholarships in database`;
    }

    const filterParts = [];
    if (search) filterParts.push(`"${search}"`);
    if (status !== "") filterParts.push(status);
    if (startDate) filterParts.push(`from: ${startDate}`);
    if (endDate) filterParts.push(`to: ${endDate}`);

    const filterText =
      filterParts.length > 0 ? ` (${filterParts.join(", ")})` : "";
    return `Showing ${total.toLocaleString()} of ${totalDbScholarships.toLocaleString()} scholarships${filterText}`;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (search) count++;
    if (status !== "") count++;
    if (startDate) count++;
    if (endDate) count++;
    return count;
  };

  const handleImageView = (imagePath: string, studentName: string) => {
    // Convert relative path to full URL using production backend
    const imageUrl = imagePath.startsWith("/uploads")
      ? `https://backend.hunarmandpunjab.org.pk${imagePath}`
      : imagePath;
    setSelectedImage(imageUrl);
    setSelectedScholarshipName(studentName);
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setSelectedScholarshipName("");
  };

  const handleStatusUpdate = (
    scholarship: Scholarship,
    status: "approved" | "rejected"
  ) => {
    setSelectedScholarship(scholarship);
    setNewStatus(status);
    setUpdateReason("");
    setStatusUpdateModalOpen(true);
  };

  const handleStatusUpdateConfirm = async () => {
    if (!selectedScholarship || !updateReason.trim()) {
      setError("Please provide a reason for the status change");
      return;
    }

    try {
      setLoading(true);
      await api.put(`/scholarships/${selectedScholarship._id}`, {
        status: newStatus,
        reason: updateReason,
      });

      // Refresh the scholarships list and stats
      await Promise.all([fetchScholarships(), fetchScholarshipStats()]);

      setStatusUpdateModalOpen(false);
      setSelectedScholarship(null);
      setUpdateReason("");
      setError("");
    } catch (err) {
      console.error("Error updating scholarship status:", err);
      setError("Failed to update scholarship status");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdateCancel = () => {
    setStatusUpdateModalOpen(false);
    setSelectedScholarship(null);
    setUpdateReason("");
  };

  // Use stats data instead of current page data
  const pendingCount = stats?.pending || 0;
  const approvedCount = stats?.approved || 0;
  const rejectedCount = stats?.rejected || 0;

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography sx={{ fontSize: "1.4rem", fontWeight: 500, color: "#059669" }}>
            HunarmandSindh Card Management
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 400, mt: 0.25 }}>
            {getFilterSummary()}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchScholarships} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <Card sx={{ flex: "1 1 200px", borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <Box sx={{ height: 3, background: "linear-gradient(90deg,#059669,#fbbf24)" }} />
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
                  Total Records
                </Typography>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 500, color: "#1e293b", lineHeight: 1 }}>
                  {stats?.total.toLocaleString() || 0}
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(5,150,105,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SchoolIcon sx={{ fontSize: 20, color: "#059669" }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: "1 1 200px", borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <Box sx={{ height: 3, background: "linear-gradient(90deg,#fbbf24,#059669)" }} />
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
                  Pending Applications
                </Typography>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 500, color: "#1e293b", lineHeight: 1 }}>
                  {pendingCount.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(251,191,36,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PendingIcon sx={{ fontSize: 20, color: "#fbbf24" }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: "1 1 200px", borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <Box sx={{ height: 3, background: "linear-gradient(90deg,#059669,#10b981)" }} />
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
                  Approved Applications
                </Typography>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 500, color: "#1e293b", lineHeight: 1 }}>
                  {approvedCount.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(5,150,105,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircleIcon sx={{ fontSize: 20, color: "#059669" }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: "1 1 200px", borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <Box sx={{ height: 3, background: "linear-gradient(90deg,#fbbf24,#f59e0b)" }} />
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
                  Rejected Applications
                </Typography>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 500, color: "#1e293b", lineHeight: 1 }}>
                  {rejectedCount.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(251,191,36,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CancelIcon sx={{ fontSize: 20, color: "#fbbf24" }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3, borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center">
            <FilterIcon sx={{ mr: 1 }} />
            <Typography sx={{ fontWeight: 500, fontSize: "0.95rem", color: "#059669" }}>Filters</Typography>
            {getActiveFilterCount() > 0 && (
              <Chip
                label={`${getActiveFilterCount()} active`}
                size="small"
                sx={{ ml: 1, bgcolor: "#059669", color: "white", fontWeight: 500 }}
              />
            )}
          </Box>
          {getActiveFilterCount() > 0 && (
            <Typography variant="body2" color="primary">
              {total.toLocaleString()} results found
            </Typography>
          )}
        </Box>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Search"
            placeholder="Name, Roll Number, Email, CNIC, Mobile, Challan"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <IconButton onClick={clearFilters} title="Clear Filters">
            <CancelIcon />
          </IconButton>

          <Tooltip title="Download filtered records as CSV">
            <IconButton
              onClick={downloadScholarships}
              disabled={loading}
              color="primary"
              sx={{ ml: 1 }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>

      {/* Scholarships Table */}
      <Card sx={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                {["Name", "Roll Number", "Email", "Mobile", "CNIC", "Challan Number", "Document", "Status", "Applied Date", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 500, fontSize: "0.75rem", color: "#059669", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #e2e8f0", py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : scholarships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No scholarships found
                  </TableCell>
                </TableRow>
              ) : (
                scholarships.map((scholarship) => (
                  <TableRow key={scholarship._id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {scholarship.fullName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{scholarship.rollNumber}</TableCell>
                    <TableCell>{scholarship.email}</TableCell>
                    <TableCell>{scholarship.mobileNumber}</TableCell>
                    <TableCell>{scholarship.cnic}</TableCell>
                    <TableCell>{scholarship.challanNumber}</TableCell>
                    <TableCell>
                      {scholarship.imagePath ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={
                              scholarship.imagePath.startsWith("/uploads")
                                ? `https://backend.hunarmandpunjab.org.pk${scholarship.imagePath}`
                                : scholarship.imagePath
                            }
                            sx={{ width: 40, height: 40, cursor: "pointer" }}
                            onClick={() =>
                              handleImageView(
                                scholarship.imagePath!,
                                scholarship.fullName
                              )
                            }
                          >
                            <ImageIcon />
                          </Avatar>
                          <Tooltip title="View Document">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleImageView(
                                  scholarship.imagePath!,
                                  scholarship.fullName
                                )
                              }
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No document
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(scholarship.status)}
                        <Chip
                          label={
                            scholarship.status.charAt(0).toUpperCase() +
                            scholarship.status.slice(1)
                          }
                          color={getStatusColor(scholarship.status) as any}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(scholarship.appliedAt)}</TableCell>
                    <TableCell>
                      {scholarship.status === "pending" ? (
                        <Box display="flex" gap={1}>
                          <Tooltip title="Approve Application">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() =>
                                handleStatusUpdate(scholarship, "approved")
                              }
                              disabled={loading}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject Application">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleStatusUpdate(scholarship, "rejected")
                              }
                              disabled={loading}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Chip
                          label={
                            scholarship.status === "approved"
                              ? "Approved"
                              : "Rejected"
                          }
                          size="small"
                          color={
                            scholarship.status === "approved"
                              ? "success"
                              : "error"
                          }
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Card>

      {/* Status Update Confirmation Modal */}
      <Dialog
        open={statusUpdateModalOpen}
        onClose={handleStatusUpdateCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {newStatus === "approved" ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
            <Typography variant="h6">
              {newStatus === "approved" ? "Approve" : "Reject"} HunarmandSindh Card
              Application
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedScholarship && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Student:</strong> {selectedScholarship.fullName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Roll Number:</strong> {selectedScholarship.rollNumber}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {selectedScholarship.email}
              </Typography>

              <Box mt={3}>
                <Typography variant="body1" color="warning.main" gutterBottom>
                  ⚠️ This action will immediately update the application status
                  and cannot be undone easily.
                </Typography>

                <TextField
                  label="Reason for status change"
                  placeholder={`Enter reason for ${
                    newStatus === "approved" ? "approving" : "rejecting"
                  } this application...`}
                  value={updateReason}
                  onChange={(e) => setUpdateReason(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  required
                  sx={{ mt: 2 }}
                  helperText="This reason will be logged for audit purposes"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusUpdateCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleStatusUpdateConfirm}
            color={newStatus === "approved" ? "success" : "error"}
            variant="contained"
            disabled={!updateReason.trim() || loading}
            startIcon={
              newStatus === "approved" ? <ApproveIcon /> : <RejectIcon />
            }
          >
            {loading
              ? "Updating..."
              : `${
                  newStatus === "approved" ? "Approve" : "Reject"
                } Application`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            HunarmandSindh Card Document - {selectedScholarshipName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: "400px" }}
            >
              <img
                src={selectedImage}
                alt="Scholarship Document"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  target.parentElement!.innerHTML = `
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 40px; color: #666;">
                      <div style="font-size: 48px; margin-bottom: 16px;">📄</div>
                      <div>Document not available</div>
                      <div style="font-size: 12px; margin-top: 8px;">File may have been moved or deleted</div>
                    </div>
                  `;
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImageModalClose} color="primary">
            Close
          </Button>
          {selectedImage && (
            <Button
              component="a"
              href={selectedImage}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              variant="contained"
            >
              Open in New Tab
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Scholarships;
